import { IAnalyticsProvider, IUserProfile, TAnalyticsProvider, TTrackCleanupFn } from "../types";

/**
 * Base class for analytics providers that handles queueing and initialization flow
 */
export abstract class BaseAnalyticsProvider implements IAnalyticsProvider {
    protected isInitialized: boolean = false;
    protected isUserIdentified: boolean = false;
    protected trackQueue: Array<() => void> = [];
    protected identityQueue: Array<() => void> = [];
    abstract providerName: TAnalyticsProvider;
    includeContextInProperties: boolean = false;

    /**
     * Initialize the analytics provider
     */
    abstract initialize(): Promise<void>;

    /**
     * Implementation-specific track method
     */
    protected abstract trackEvent(
        eventName: string,
        properties?: Record<string, any>,
        cleanup?: TTrackCleanupFn,
    ): void;

    /**
     * Implementation-specific identify method
     */
    protected abstract identifyUser(userId: string): void;

    /**
     * Implementation-specific user profile setting method
     */
    protected abstract setUserProfileImpl(profile: IUserProfile): void;

    /**
     * Implementation-specific reset method
     */
    protected abstract resetImpl(): void;

    /**
     * Add a function to a queue or execute it immediately based on condition
     */
    protected enqueue(queue: Array<() => void>, condition: boolean, fn: () => void): void {
        if (condition) {
            fn();
        } else {
            queue.push(fn);
        }
    }

    /**
     * Run all functions in a queue
     */
    protected runQueue(queue: Array<() => void>): void {
        while (queue.length > 0) {
            const fn = queue.shift();
            fn?.();
        }
    }

    /**
     * Mark as initialized and process queued identity functions
     */
    protected handleInitialized(): void {
        this.isInitialized = true;
        this.runQueue(this.identityQueue);
    }

    /**
     * Mark user as identified and process queued tracking functions
     */
    protected handleIdentified(userId: string): void {
        this.identifyUser(userId);
        this.isUserIdentified = true;
        this.runQueue(this.trackQueue);
    }

    /**
     * Public method to track an event
     */
    track(eventKey: string, properties?: Record<string, any>, cleanup?: TTrackCleanupFn): void {
        this.enqueue(this.trackQueue, this.isUserIdentified, () =>
            this.trackEvent(eventKey, properties, cleanup),
        );
    }

    /**
     * Public method to identify a user
     */
    identify(userId: string): void {
        this.enqueue(this.identityQueue, this.isInitialized, () => this.handleIdentified(userId));
    }

    /**
     * Public method to set user profile
     */
    setUserProfile(profile: IUserProfile): void {
        this.enqueue(this.identityQueue, this.isInitialized, () => this.setUserProfileImpl(profile));
    }

    /**
     * Public method to reset user data
     */
    reset(): void {
        this.enqueue(this.trackQueue, this.isUserIdentified, () => {
            this.resetImpl();
            this.isUserIdentified = false;
        });
    }
}
